import React, { FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  FitnessActivity,
  PublishingSource,
} from '../../../../types/__generated__';
import { useTimeline } from '../../../hooks/timeline/useTimeline';
import ConfirmationModal from '../../ConfirmationModal/ConfirmationModal';
import Modal from '../../Modal/Modal';
import { Body } from './Body';
import { Footer } from './Footer';
import { Header } from './Header';

import styles from '../Timeline.scss';

export interface TimelineEntryProps {
  id: string;
  fitnessActivity?: FitnessActivity | null;
  refreshRequestedTime: string | Date | null;
  message?: string | null;
  media?: any;
  createdAt: string;
  publishingSource?: PublishingSource | null;
}

export const TimelineEntry: FunctionComponent<TimelineEntryProps> = ({
  id,
  fitnessActivity,
  refreshRequestedTime,
  createdAt,
  media,
  message,
  publishingSource,
}) => {
  const { t } = useTranslation('common', { useSuspense: false });
  const [confirmationModalIsOpen, setConfirmationModalIsOpen] = useState(false);
  const { timeline, deleteTimelineEntry } = useTimeline();

  const isStravaActivity = publishingSource === PublishingSource.Strava;

  const handleOpenConfirmationModal = () => {
    setConfirmationModalIsOpen(true);
  };

  const handleCloseConfirmationModal = () => {
    setConfirmationModalIsOpen(false);
  };

  const handleDeleteTimelineEntry = async () => {
    await deleteTimelineEntry(id, timeline?.pageInfo);
  };

  return (
    <div className={styles.timeline} data-qa="timeline-entry">
      <Header
        id={id}
        fitnessActivity={fitnessActivity}
        createdAt={createdAt}
        isStravaActivity={isStravaActivity}
        refreshRequestedTime={refreshRequestedTime}
        onDeleteEntry={handleOpenConfirmationModal}
      />
      <Body fitnessActivity={fitnessActivity} media={media} message={message} />
      <Footer
        fitnessActivity={fitnessActivity}
        media={media}
        message={message}
      />
      {confirmationModalIsOpen && (
        <Modal
          onCloseRequest={handleCloseConfirmationModal}
          data-qa={'delete-confirmation-modal'}
        >
          <ConfirmationModal
            modalTitle={
              fitnessActivity
                ? t('timeline.deleteTimelineEntry.modal.fitnessTitle')
                : t('timeline.deleteTimelineEntry.modal.nonFitnessTitle')
            }
            confirmButtonText={t('timeline.deleteTimelineEntry.modal.confirm')}
            dismissButtonText={t('timeline.deleteTimelineEntry.modal.dismiss')}
            onDismiss={handleCloseConfirmationModal}
            onConfirm={handleDeleteTimelineEntry}
          >
            <p>
              {fitnessActivity
                ? t(
                    'timeline.deleteTimelineEntry.modal.fitnessConfirmationMessage',
                  )
                : t(
                    'timeline.deleteTimelineEntry.modal.nonFitnessConfirmationMessage',
                  )}
            </p>
          </ConfirmationModal>
        </Modal>
      )}
    </div>
  );
};
TimelineEntry.displayName = 'TimelineEntry';
